import { ChangeDetectionStrategy, Component, inject, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { filter, map } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { FooterComponent, NotifyComponent } from "@husbanken/designsystem-angular-core";
import { AppHeaderComponent } from "./core/components/header/header.component";
import { AuthService, NotifyService } from "@husbanken/frontend-starter";
import { ColorThemeService } from "./services/colortheme/ColorThemeService";

const SPRAAK_COOKIE_KEY = "HbLanguage";
const DEFAULT_SPRAAK = "nb";

@Component({
  selector: "bko-root",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AppHeaderComponent, RouterOutlet, NotifyComponent, FooterComponent],
  styleUrl: "./app.component.scss",
  template: `
    <div class="hb-page">
      <div class="pb-page-header">
        @if (showHeader()) {
          <bko-header (changeLanguage)="changeLanguage($event)" (logout)="logout()" />
        }
      </div>

      <main class="hb-page-main" [class]="colorThemeService.colorTheme()">
        <div class="hb-container hb-container--width-lg">
          <hb-notify [notificationSource]="notifyService" />
          <router-outlet />
        </div>
      </main>

      <div class="hb-page-footer">
        <hb-footer />
      </div>
    </div>
  `,
})
export class AppComponent {
  private authService = inject(AuthService);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  public notifyService = inject(NotifyService);

  // Use colorThemeService to set theme
  // Included here we want all pages to have a "default" theme
  // Currently also necessary due to the html here having a small container around the main content of the page.
  colorThemeService = inject(ColorThemeService);

  showHeader: Signal<boolean> = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let route = this.route;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return !route.snapshot.data["hideHeader"];
      }),
    ),
    { initialValue: true },
  );

  constructor() {
    this.translateService.use(localStorage.getItem(SPRAAK_COOKIE_KEY) ?? DEFAULT_SPRAAK);
  }

  public changeLanguage(languageCode: string): void {
    this.translateService.use(languageCode);
    localStorage.setItem(SPRAAK_COOKIE_KEY, languageCode);
  }

  public logout(): void {
    this.authService.logout();
  }
}
